import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Check, X, Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { createLabel, updateLabel, deleteLabel, setLabelColor } from "@/lib/offlineStore";
import { LABEL_COLORS, labelStyle } from "@/lib/labelColors";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useT } from "@/i18n/LanguageContext";

export default function LabelsDialog({ open, onOpenChange, labels, onChanged }) {
  const { t } = useT();
  const [name, setName] = useState("");
  const [editing, setEditing] = useState({});

  useEffect(() => {
    if (!open) { setEditing({}); setName(""); }
  }, [open]);

  const add = async () => {
    const v = name.trim();
    if (!v) return;
    await createLabel(v);
    setName("");
    toast.success(t("labelsDialog.created"));
    onChanged();
  };

  const save = async (id) => {
    const v = (editing[id] || "").trim();
    if (!v) return;
    await updateLabel(id, v);
    setEditing((e) => { const n = { ...e }; delete n[id]; return n; });
    toast.success(t("labelsDialog.renamed"));
    onChanged();
  };

  const pickColor = async (id, color) => {
    await setLabelColor(id, color);
    onChanged();
  };

  const remove = async (id) => {
    await deleteLabel(id);
    toast.success(t("labelsDialog.deleted"));
    onChanged();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="labels-dialog">
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute top-3 right-3 h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-800"
          data-testid="labels-close-btn"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <DialogHeader>
          <DialogTitle className="font-display">{t("labelsDialog.title")}</DialogTitle>
          <DialogDescription className="sr-only">{t("labelsDialog.title")}</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <Input
            placeholder={t("labelsDialog.placeholder")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            data-testid="label-new-input"
          />
          <Button onClick={add} data-testid="label-add-btn" className="rounded-full" size="icon">
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-1 max-h-80 overflow-y-auto pr-1">
          {labels.length === 0 ? (
            <p className="text-sm text-stone-500 py-6 text-center">{t("labelsDialog.empty")}</p>
          ) : (
            labels.map((l) => {
              const isEditing = editing[l.id] !== undefined;
              return (
                <div key={l.id} className="flex items-center gap-2 py-1" data-testid={`label-row-${l.name}`}>
                  {isEditing ? (
                    <>
                      <Input
                        value={editing[l.id]}
                        onChange={(e) => setEditing((prev) => ({ ...prev, [l.id]: e.target.value }))}
                        onKeyDown={(e) => e.key === "Enter" && save(l.id)}
                        autoFocus
                        data-testid={`label-edit-input-${l.name}`}
                      />
                      <Button size="icon" variant="ghost" onClick={() => save(l.id)} data-testid={`label-save-${l.name}`}>
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => setEditing((e) => { const n = { ...e }; delete n[l.id]; return n; })}>
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className={`h-5 w-5 rounded-full shrink-0 ring-2 ring-offset-2 ring-offset-background ring-transparent hover:ring-stone-300 transition-shadow ${labelStyle(labels, l.name).dot}`} data-testid={`label-color-${l.name}`} aria-label={`Colour for ${l.name}`} />
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-2" align="start" data-testid={`label-color-popover-${l.name}`}>
                          <div className="grid grid-cols-6 gap-1.5">
                            {Object.entries(LABEL_COLORS).map(([key, c]) => (
                              <button key={key} onClick={() => pickColor(l.id, key)} className={`h-6 w-6 rounded-full ${c.dot} ${l.color === key ? "ring-2 ring-offset-2 ring-offset-background ring-stone-900 dark:ring-white" : ""}`} data-testid={`label-color-${l.name}-${key}`} aria-label={key} />
                            ))}
                            <button onClick={() => pickColor(l.id, null)} className="h-6 w-6 rounded-full border border-dashed border-stone-400 inline-flex items-center justify-center" data-testid={`label-color-${l.name}-none`} aria-label="No colour"><X className="h-3 w-3" /></button>
                          </div>
                        </PopoverContent>
                      </Popover>
                      <span className="flex-1 text-sm">{l.name}</span>
                      <Button size="icon" variant="ghost" onClick={() => setEditing((e) => ({ ...e, [l.id]: l.name }))} data-testid={`label-edit-${l.name}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(l.id)} data-testid={`label-delete-${l.name}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
